import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { usePurchasePhoneNumberMutation } from "@/store/rest_api";
import AvailablePhoneNumber from "@/model/interface/AvailablePhoneNumber";
import { formatMoneyAmount } from "@/util/sums";
import { apiErrorMessage } from "@/util/apiError";
import { toast } from "sonner";

interface BuyPhoneNumberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableNumber: AvailablePhoneNumber | null;
  numberType: "local" | "tollfree";
  included: boolean;
  isTrial: boolean;
  onPurchased: (number: string) => void;
}

export function BuyPhoneNumberDialog({
  open,
  onOpenChange,
  availableNumber,
  numberType,
  included,
  isTrial,
  onPurchased,
}: BuyPhoneNumberDialogProps) {
  const [createDefaultFlow, setCreateDefaultFlow] = useState<boolean>(true);
  const [purchaseNumber, { isLoading }] = usePurchasePhoneNumberMutation();

  async function buyNumber() {
    if (!availableNumber) {
      return;
    }

    try {
      await purchaseNumber({
        number: availableNumber.number,
        location: availableNumber.location,
        region: availableNumber.region,
        capabilities: JSON.stringify(availableNumber.capabilities),
        monthlyCost: availableNumber.monthlyCost,
        type: numberType === "tollfree" ? "TOLL_FREE" : "LOCAL",
        createDefaultFlow,
      }).unwrap();

      onPurchased(availableNumber.number);
      toast.success(
        createDefaultFlow
          ? `${availableNumber.number} purchased and answering on its default call flow`
          : `${availableNumber.number} purchased successfully`,
      );
      onOpenChange(false);
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to purchase phone number"));
    }
  }

  if (!availableNumber) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Buy {availableNumber.number}</DialogTitle>
          <DialogDescription>
            {availableNumber.location}
            {availableNumber.region ? `, ${availableNumber.region}` : ""}.{" "}
            {included
              ? `Included in your ${isTrial ? "trial" : "plan"}.`
              : `${formatMoneyAmount(availableNumber.monthlyCost)} per month. The first month is charged today.`}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-border p-4">
          <div className="flex items-start gap-3">
            <Checkbox
              id="create-default-flow"
              checked={createDefaultFlow}
              onCheckedChange={(checked) => setCreateDefaultFlow(checked === true)}
              disabled={isLoading}
            />
            <div className="space-y-1">
              <Label htmlFor="create-default-flow" className="font-semibold">
                Set up a default call flow
              </Label>
              <p className="text-sm text-muted-foreground">
                Callers ring your devices for 30 seconds, then leave a voicemail. Without it, this number will not
                answer calls until you assign a flow to it.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={buyNumber} disabled={isLoading}>
            {isLoading ? "Buying..." : "Buy number"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
